import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, FormGroup, Input, Label, Modal, Row } from 'reactstrap';
import api from "../../../../services/api";
import _ from 'lodash';
import { useMsal } from '@azure/msal-react';
import Loader from '../../../loaders';
import { Redirect, useHistory, useParams } from "react-router-dom";
import TerminationForm from './TerminationForm';
import TerminationApprovalPanel from './TerminationApprovalPanel';
import { checkApprover, composeDataToSend, returnToApprovalList, returnToList, validate } from './TerminationUtils';
import { roles } from '../../../../auth/roles';

const apiUrl = "termination";

export default function TerminationFinalForm(props) {
    const {id, approvalId} = useParams();
    const urlParams = new URLSearchParams(window.location.search);
    const isApproval = urlParams.get("isApproval") ?? false;

    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [loadingCheckApprover, setLoadingCheckApprover] = useState(false);
    const [itemInEdit, setItemInEdit] = useState({});
    const [shouldCheckApprover, setShouldCheckApprover] = useState(false);
    const [initialApprover, setInitialApprover] = useState(null);
    const [isApproverExists, setIsApproverExists] = useState(false);
    const [validationObj, setValidationObj] = useState([]);
    const [showValidation, setShowValidation] = useState(false);
    const [canSave, setCanSave] = useState(false);
    const [showDeclineDialog, setShowDeclineDialog] = useState(false);
    const { instance } = useMsal();
    const user = instance.getActiveAccount();
    const history = useHistory();

    const hasFullAccess = useMemo(() => {
        return _.intersection([roles.HR], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    const hasAccess = useMemo(() => {
        return _.intersection([roles.HR, roles.Manager, roles.Admin], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    useEffect(() => {
        if (!itemInEdit?.lastDayEmploymentDate)
            return;

        if (itemInEdit?.lastDayWorkDate)
            return;

        setItemInEdit({...itemInEdit, lastDayWorkDate: itemInEdit.lastDayEmploymentDate})
    }, [itemInEdit?.lastDayEmploymentDate]);

    useEffect(() => {
        if (!shouldCheckApprover)
            return;

        setShouldCheckApprover(false);
        setLoadingCheckApprover(true);
        checkApprover(itemInEdit, setItemInEdit, setInitialApprover, setIsApproverExists, validationObj, setValidationObj, user, api, apiUrl, setLoadingCheckApprover, _);
    }, [shouldCheckApprover]);

    const scrollToValidation = useCallback(() => {
        if (!validationObj?.length)
            return;

        setTimeout(function () { //Start the timer
            const scrollObject = document.getElementById("validationAlert");
            if (!scrollObject) return;
            scrollObject.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }.bind(this), 500);
    }, [validationObj]);

    useEffect(() => {
        if (!showValidation)
            return;

        setShowValidation(false);
        scrollToValidation();
    }, [showValidation]);

    const handleSave = useCallback((saveOnApproval = false, approved = false) => {
        const validationResult = validate(itemInEdit, true, isApproval);

        if (isApproval && !itemInEdit?.approverName && !itemInEdit?.autoApproved)
            validationResult.push({
                type: "error",
                message: "Approver has not been set. Please contact your administrator.",
            });

        if (isApproval && !approvalId)
            validationResult.push({
                type: "error",
                message: "Approval Id is not found. Please contact your administrator.",
            });

        if (isApproval && saveOnApproval && !hasFullAccess && user?.username?.toLowerCase() !== itemInEdit?.approverEmail?.toLowerCase() && !itemInEdit?.autoApproved)
            validationResult.push({
                type: "error",
                message: "This action can only be executed by Approver or HR.",
            });

        setValidationObj([...validationResult]);

        if (validationResult?.length)
        {
            setShowValidation(true);

            if (showDeclineDialog)
                setShowDeclineDialog(false);

            return;
        }

        setLoading(true);
        const url = `${process.env.REACT_APP_WEB_API_BASE_URI}/${apiUrl}/${itemInEdit.terminationId}`;
        const method = "PUT";

        api({
            url,
            method,
            data: composeDataToSend(itemInEdit, false, !isApproval, saveOnApproval, approvalId, approved),
        })
            .then(() => {
                setLoading(false);

                if (!hasFullAccess)
                {
                    history.push("/");
                    return;
                }
                
                if (isApproval)
                {
                    returnToApprovalList(history);
                    return;
                }

                returnToList(history);
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [canSave, itemInEdit, id, approvalId, isApproval, history, showDeclineDialog, hasFullAccess, user]);

    const footer = useMemo(() => {
        return (
            <Row>
                <Col xs={12} className="d-flex flex-wrap justify-content-end">
                    <Row className="w-100 m-0 p-0 mb-4">
                        <Col xs={3} className="d-flex flex-wrap justify-content-start align-items-center m-0 p-0">
                            <Button
                                color="info"
                                type="button"
                                onClick={() => {
                                    if (!hasFullAccess)
                                    {
                                        history.push("/");
                                        return;
                                    }
                                    
                                    if (isApproval)
                                    {
                                        returnToApprovalList(history);
                                        return;
                                    }
                    
                                    returnToList(history);
                                }}
                            >
                                Close
                            </Button>
                        </Col>
                        <Col xs={9} className="d-flex flex-wrap justify-content-end align-items-center m-0 p-0">
                            <Button
                                color="default"
                                type="button"
                                onClick={() => handleSave()}
                                disabled={itemInEdit?.status?.lookupValue === "Declined" || itemInEdit?.status?.lookupValue === "Final"}
                            >
                            {
                                !isApproval ? "Finalize" : "Save"
                            }
                            </Button>
                        {
                            isApproval ?
                            <>
                                <Button
                                    color="warning"
                                    type="button"
                                    onClick={() => setShowDeclineDialog(true)}
                                    disabled={itemInEdit?.status?.lookupValue !== "Pending Approval"}
                                >
                                    Reject
                                </Button>
                                <Button
                                    color="default"
                                    type="button"
                                    onClick={() => handleSave(true, true)}
                                    disabled={itemInEdit?.status?.lookupValue !== "Pending Approval"}
                                >
                                    Approve
                                </Button>
                            </>
                            :
                            null
                        }
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    }, [itemInEdit, history, canSave, hasFullAccess]);

    if (!hasAccess)
        return (
            <Redirect
                to={{
                    pathname: '/unauthorized',
                    state: {
                        from: "exitformfinal"
                    }
                }}
            />
        );

    return (
        <section className="main">
            {(loading || formLoading || loadingCheckApprover) && <Loader />}
            <Container fluid>
                <Row className="m-0 p-0">
                    <Col xs={10}>
                        <TerminationForm
                            id={id}
                            approvalId={approvalId}
                            isApproval={isApproval}
                            itemInEdit={itemInEdit}
                            setItemInEdit={setItemInEdit}
                            setLoading={setFormLoading}
                            setCanSave={setCanSave}
                            setShouldCheckApprover={setShouldCheckApprover}
                            isFinal={true}
                            validationObj={validationObj}
                            footer={footer}
                        />
                    </Col>
                    <Col xs={2} className="bg-secondary lrfstatusbox" style={{minHeight: "30vh"}}>
                        <TerminationApprovalPanel
                            itemInEdit={itemInEdit}
                            loading={formLoading}
                            initialApprover={initialApprover}
                            isApproverExists={isApproverExists}
                        />
                    </Col>
                </Row>
            </Container>
        {
            showDeclineDialog &&
            <Modal
                isOpen={true}
                className="modal-lg"
                modalClassName="db-example-modal-lg"
            >
                <div
                    className="modal-header"
                >
                    <h5
                        className="modal-title"
                    >
                        Exit Form Request Rejection Dialog
                    </h5>
                    <button
                        aria-label="Close"
                        className="close"
                        onClick={() => setShowDeclineDialog(false)}
                        type="button"
                    >
                        <span aria-hidden={true}><i className="fas fa-times-circle" /></span>
                    </button>
                </div>
                <div
                    className="modal-body"
                >
                    <FormGroup>
                        <Label>
                            Reason
                            <span className="text-danger">*</span>
                        </Label>
                        <Input
                            className="w-100"
                            value={itemInEdit?.approvalReason ?? ""}
                            onChange={(e) => setItemInEdit({...itemInEdit, approvalReason: e.target.value})}
                            type="textarea"
                            rows={3}
                        />
                    </FormGroup>
                </div>
                <div
                    className="modal-footer"
                >
                    <Button
                        color="secondary"
                        onClick={() => setShowDeclineDialog(false)}
                        type="button"
                    >
                        Close
                    </Button>
                    <Button
                        color="warning"
                        onClick={() => handleSave(true, false)}
                        type="button"
                        disabled={!itemInEdit?.approvalReason || itemInEdit?.approvalReason === ""}
                    >
                        Reject
                    </Button>
                </div>
            </Modal>
        }
        </section>
    );
};